// 제조 관리 타입 정의

// BOM 상태
export type BOMStatus = 'active' | 'draft' | 'inactive';

// 작업지시 상태
export type WorkOrderStatus =
  | 'draft'
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'stopped'
  | 'cancelled';

// 작업지시 우선순위
export type WorkOrderPriority = 'low' | 'medium' | 'high' | 'urgent';

// 작업 공정 상태
export type OperationStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

// 품목 (BOM용)
export interface BOMItem {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  rate: number;
  amount: number;
  description?: string;
}

// BOM (Bill of Materials)
export interface BOM {
  id: string;
  code: string;
  name: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  status: BOMStatus;
  isDefault: boolean;
  qty: number;
  uom: string;
  items: BOMItem[];
  operations: BOMOperation[];
  totalCost: number;
  operationCost: number;
  materialCost: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// BOM 공정
export interface BOMOperation {
  id: string;
  sequence: number;
  operationName: string;
  workstation: string;
  timeInMins: number;
  hourRate: number;
  operationCost: number;
  description?: string;
}

// 작업지시
export interface WorkOrder {
  id: string;
  code: string;
  bomId: string;
  bomCode: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  status: WorkOrderStatus;
  priority: WorkOrderPriority;
  qty: number;
  producedQty: number;
  pendingQty: number;
  uom: string;
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  warehouse: string;
  operations: WorkOrderOperation[];
  salesOrderCode?: string;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 작업지시 공정
export interface WorkOrderOperation {
  id: string;
  sequence: number;
  operationName: string;
  workstation: string;
  status: OperationStatus;
  plannedTime: number;
  actualTime: number;
  completedQty: number;
  startTime?: string;
  endTime?: string;
}

// 작업장
export interface Workstation {
  id: string;
  code: string;
  name: string;
  workstationType: string;
  hourRate: number;
  capacity: number;
  isActive: boolean;
  description?: string;
}

// 생산 입고
export interface StockEntry {
  id: string;
  code: string;
  entryType: 'manufacture' | 'material_transfer' | 'material_issue';
  workOrderId?: string;
  workOrderCode?: string;
  postingDate: string;
  items: StockEntryItem[];
  totalQty: number;
  totalAmount: number;
  remarks?: string;
  createdAt: string;
}

export interface StockEntryItem {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  rate: number;
  amount: number;
  sourceWarehouse?: string;
  targetWarehouse?: string;
}

// 상태 레이블 및 색상
export const bomStatusConfig: Record<BOMStatus, { label: string; color: string }> = {
  active: { label: '활성', color: 'green' },
  draft: { label: '초안', color: 'gray' },
  inactive: { label: '비활성', color: 'red' },
};

export const workOrderStatusConfig: Record<WorkOrderStatus, { label: string; color: string }> = {
  draft: { label: '초안', color: 'gray' },
  not_started: { label: '대기', color: 'blue' },
  in_progress: { label: '진행중', color: 'yellow' },
  completed: { label: '완료', color: 'green' },
  stopped: { label: '중단', color: 'orange' },
  cancelled: { label: '취소', color: 'red' },
};

export const workOrderPriorityConfig: Record<WorkOrderPriority, { label: string; color: string }> = {
  low: { label: '낮음', color: 'gray' },
  medium: { label: '보통', color: 'blue' },
  high: { label: '높음', color: 'orange' },
  urgent: { label: '긴급', color: 'red' },
};

export const operationStatusConfig: Record<OperationStatus, { label: string; color: string }> = {
  pending: { label: '대기', color: 'gray' },
  in_progress: { label: '진행중', color: 'yellow' },
  completed: { label: '완료', color: 'green' },
  skipped: { label: '건너뜀', color: 'orange' },
};
